import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams, RowNode } from 'ag-grid-enterprise';

const AVATAR_COLORS = [
  '#2469A0', '#7B61FF', '#E85D75', '#F59E0B', '#10B981',
  '#6366F1', '#EC4899', '#14B8A6', '#F97316', '#8B5CF6',
];

function nameToColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function initials(name: string): string {
  return name.trim().split(/\s+/).slice(0, 2).map(w => w[0].toUpperCase()).join('');
}

/**
 * Shared employee cell renderer.
 *
 * Modes (controlled via colDef.cellRendererParams):
 *   showExpander: true  → shows chevron + expand/collapse + "New" badge for PROBATION (leave-balance)
 *   showExpander: false → avatar + name + role only (leave-request, default)
 */
@Component({
  selector: 'ers-employee-cell-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="emp-cell" [class.emp-cell--expandable]="showExpander" (click)="onCellClick($event)">

      <!-- Chevron (leave-balance only) -->
      <span *ngIf="showExpander" class="material-icons emp-chevron" [class.expanded]="isExpanded">
        chevron_right
      </span>

      <!-- Avatar: photo or initials fallback -->
      <img
        *ngIf="avatar"
        [src]="avatar"
        [alt]="name"
        class="emp-avatar"
        (error)="onImgError()"
      />
      <div
        *ngIf="!avatar"
        class="emp-avatar emp-initials"
        [style.background]="avatarColor"
      >{{ initialsText }}</div>

      <!-- Name + badge + role -->
      <div class="emp-info">
        <div class="emp-name-row">
          <span class="emp-name">{{ name }}</span>
          <span *ngIf="showNewBadge" class="emp-badge">
            <span class="material-icons emp-badge-icon">star</span>
            New
          </span>
        </div>
        <div class="emp-role">{{ role }}</div>
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      overflow: hidden;
    }
    .emp-cell {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 4px 0;
      overflow: hidden;
    }
    .emp-cell--expandable { cursor: pointer; }

    /* Chevron */
    .emp-chevron {
      font-size: 20px;
      color: var(--lm-muted, #64748b);
      transition: transform 0.2s ease;
      user-select: none;
      flex-shrink: 0;
      &.expanded { transform: rotate(90deg); }
      &:hover { color: var(--lm-text, #1e293b); }
    }

    /* Avatar */
    .emp-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }
    .emp-initials {
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }

    /* Info block */
    .emp-info {
      display: flex;
      flex-direction: column;
      gap: 1px;
      line-height: 1.2;
      min-width: 0;
    }
    .emp-name-row {
      display: flex;
      align-items: center;
      gap: 6px;
      min-width: 0;
    }
    .emp-name {
      font-weight: 600;
      font-size: 12.5px;
      color: var(--lm-text, #1e293b);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .emp-role {
      font-size: 10.5px;
      color: var(--lm-muted, #64748b);
      margin-top: -1px;
    }

    /* "New" / probation badge */
    .emp-badge {
      display: inline-flex;
      align-items: center;
      gap: 3px;
      font-size: 10px;
      font-weight: 700;
      color: #166534;
      background: #dcfce7;
      border: 1px solid #86efac;
      padding: 1px 4px;
      border-radius: 4px;
      line-height: 14px;
      flex-shrink: 0;
      white-space: nowrap;
    }
    .emp-badge-icon {
      font-size: 12px;
      color: inherit;
      line-height: 1;
    }
  `],
})
export class EmployeeCellRendererComponent implements ICellRendererAngularComp {
  private params!: ICellRendererParams;
  private expandedListener?: () => void;

  name         = '';
  role         = '';
  avatar       = '';
  initialsText = '';
  avatarColor  = '#2469A0';
  showExpander = false;
  showNewBadge = false;
  isExpanded   = false;

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.showExpander = !!(params.colDef as any)?.cellRendererParams?.showExpander;
    this.setData(params);

    if (this.showExpander) {
      const node = params.node as RowNode;
      this.expandedListener = () => { this.isExpanded = node.expanded || false; };
      node.addEventListener('expandedChanged', this.expandedListener);
    }
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.setData(params);
    return true;
  }

  destroy(): void {
    if (this.showExpander && this.expandedListener) {
      (this.params?.node as RowNode)?.removeEventListener('expandedChanged', this.expandedListener);
    }
  }

  onCellClick(event: MouseEvent): void {
    if (!this.showExpander) return;
    event.stopPropagation();
    this.params.node.setExpanded(!this.params.node.expanded);
  }

  onImgError(): void {
    this.avatar = '';
  }

  private setData(params: ICellRendererParams): void {
    const d: any = params.data || {};
    this.name         = d.employeeName   || '';
    this.role         = d.employeeRole   || '';
    this.avatar       = d.employeeAvatar || '';
    this.initialsText = this.name ? initials(this.name) : '?';
    this.avatarColor  = this.name ? nameToColor(this.name) : '#94a3b8';
    this.isExpanded   = params.node?.expanded || false;
    this.showNewBadge = d.employmentStatus === 'PROBATION';
  }
}

